import React, { useRef, useState } from 'react';
import { Alert, Button, DatePicker, Descriptions, Form, Input, InputNumber, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import {
  useApporteursApi, type TApporteur, type TEnfantAttribue, type THistoriqueEnfant, type TLigneHistorique,
} from '../../../api/paiement/apporteurs';
import { nomAnimateur } from './EcolesAssociees';

// Corrections d'attribution : un enfant passe à un autre animateur (ou à
// personne) à partir d'une date, éventuellement passée. Motif obligatoire,
// auteur et date journalisés ; les mois déjà écoulés qu'une correction change
// sont signalés « modifié après coup » dans le tableau mensuel.

const erreurServeur = (err: unknown): string | undefined =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error;

type CorrectionForm = {
  apporteur_id?: number;
  effet_le: Dayjs;
  motif: string;
};

const Corrections: React.FC<{ apporteurs: TApporteur[]; peutGerer: boolean }> = ({ apporteurs, peutGerer }) => {
  const { t } = useTranslation();
  const api = useApporteursApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [form] = Form.useForm<CorrectionForm>();

  const [enfantId, setEnfantId] = useState<number | null>(null);
  const [historique, setHistorique] = useState<THistoriqueEnfant | null>(null);
  const [loading, setLoading] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const parId = new Map(apporteurs.map((a) => [a.id, a]));
  const nom = (id?: number) => (id ? nomAnimateur(parId.get(id)) || `#${id}` : t('apporteurs.corrections.personne', 'Personne'));

  const sources: Record<TEnfantAttribue['source'], string> = {
    inscription_parent: t('apporteur.liste.sourceInscription', 'Inscription du parent'),
    paiement: t('apporteur.liste.sourcePaiement', 'Code au paiement'),
    ecole: t('apporteur.liste.sourceEcole', 'École associée'),
    correction: t('apporteur.liste.sourceCorrection', 'Correction'),
  };

  const afficher = async () => {
    if (!enfantId) return;
    setLoading(true);
    try {
      setHistorique(await apiRef.current.historiqueEnfant(enfantId));
    } catch (err) {
      setHistorique(null);
      message.error(erreurServeur(err) || t('apporteurs.corrections.chargementErreur', "Échec du chargement de l'historique"));
    } finally {
      setLoading(false);
    }
  };

  const corriger = async (v: CorrectionForm) => {
    if (!historique) return;
    setEnCours(true);
    try {
      const h = await api.corriger({
        enfant_id: historique.enfant_id,
        apporteur_id: v.apporteur_id ?? null,
        effet_le: v.effet_le.startOf('day').toISOString(),
        motif: v.motif,
      });
      setHistorique(h);
      form.resetFields();
      message.success(t('apporteurs.corrections.enregistree', 'Correction enregistrée'));
    } catch (err) {
      message.error(erreurServeur(err) || t('apporteurs.corrections.erreur', "Échec de la correction"));
    } finally {
      setEnCours(false);
    }
  };

  const colonnes: ColumnsType<TLigneHistorique> = [
    { title: t('apporteurs.admin.animateur', 'Animateur'), key: 'animateur', render: (_, l) => nom(l.apporteur_id) },
    { title: t('apporteurs.corrections.source', 'Origine'), key: 'source', render: (_, l) => sources[l.source] ?? l.source },
    {
      title: t('apporteurs.ecoles.periode', 'Période'),
      key: 'periode',
      render: (_, l) => (
        <Space>
          <span>{dayjs(l.debut).format('DD/MM/YYYY')}</span>
          <span>→</span>
          {l.fin ? <span>{dayjs(l.fin).format('DD/MM/YYYY')}</span> : <Tag color="green">{t('apporteurs.ecoles.enCours', 'en cours')}</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'etat',
      render: (_, l) => l.annulee ? <Tag>{t('apporteurs.corrections.annulee', 'annulée par une correction')}</Tag> : null,
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={t('apporteurs.corrections.explication',
          "Une correction peut être rétroactive. Les mois déjà écoulés qu'elle change sont signalés « modifié après coup » dans le tableau mensuel.")}
      />
      <Space wrap>
        <InputNumber
          min={1}
          style={{ width: 200 }}
          placeholder={t('apporteurs.corrections.enfantId', "Identifiant de l'enfant")}
          value={enfantId}
          onChange={(v) => setEnfantId(typeof v === 'number' ? v : null)}
          onPressEnter={afficher}
        />
        <Button onClick={afficher} loading={loading} disabled={!enfantId}>
          {t('apporteurs.corrections.afficher', "Afficher l'historique")}
        </Button>
      </Space>

      {historique && (
        <>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {[historique.prenom, historique.nom].filter(Boolean).join(' ') || `#${historique.enfant_id}`}
          </Typography.Title>
          <Table rowKey="id" columns={colonnes} dataSource={historique.attributions} pagination={false} scroll={{ x: true }} />
          {historique.corrections.length > 0 && (
            <Descriptions column={1} size="small" bordered title={t('apporteurs.corrections.titre', 'Corrections')}>
              {historique.corrections.map((c) => (
                <Descriptions.Item key={c.id} label={dayjs(c.cree_le).format('DD/MM/YYYY HH:mm')}>
                  {t('apporteurs.corrections.ligne', '{{ancien}} → {{nouvel}} à partir du {{effet}} : {{motif}}', {
                    ancien: nom(c.ancien_apporteur_id),
                    nouvel: nom(c.nouvel_apporteur_id),
                    effet: dayjs(c.effet_le).format('DD/MM/YYYY'),
                    motif: c.motif,
                  })}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}

          {peutGerer && (
            <Form form={form} layout="vertical" onFinish={corriger} style={{ maxWidth: 520 }}>
              <Form.Item name="apporteur_id" label={t('apporteurs.corrections.nouvelAnimateur', 'Nouvel animateur (vide : personne)')}>
                <Select allowClear options={apporteurs.map((a) => ({ value: a.id, label: nomAnimateur(a) }))} />
              </Form.Item>
              <Form.Item
                name="effet_le"
                label={t('apporteurs.corrections.effet', "Date d'effet")}
                rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}
              >
                <DatePicker format="DD/MM/YYYY" disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
              </Form.Item>
              <Form.Item
                name="motif"
                label={t('apporteurs.corrections.motif', 'Motif')}
                rules={[
                  { required: true, whitespace: true, message: t('apporteurs.corrections.motifRequis', 'Motif obligatoire') },
                  { min: 3, message: t('apporteurs.corrections.motifRequis', 'Motif obligatoire') },
                ]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={enCours}>
                {t('apporteurs.corrections.enregistrer', 'Enregistrer la correction')}
              </Button>
            </Form>
          )}
        </>
      )}
    </Space>
  );
};

export default Corrections;
